import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Modal } from '../receiving/Modal';
import { formatDate, formatEUR } from '../../lib/format';
import { describeApiError } from '../../lib/errorMessages';
import { enumLabel } from '../../lib/enumLabel';
import { useAudioFeedback } from '../../hooks/useAudioFeedback';
import { cn } from '../../utils/cn';

/**
 * The order sheet's top half (order-sheet prototype, 2026-10): what is handed over, what is in the
 * bin, what is still expected — and ONE main action that says what to do next. The figures are
 * the server's: counts come from the lines, money from /payment-quote (INV-05).
 */

export interface SheetLine {
  id: string;
  productId: string | null;
  lineType?: 'GOODS' | 'SHIPPING';
  productSku?: string | null;
  productName?: string | null;
  productEan?: string | null;
  productEan2?: string | null;
  manufacturerRef?: string | null;
  quantity: number;
  quantityDelivered?: number;
  quantityShort?: number;
  source: 'INTERNAL' | 'EXTERNAL';
  status: string;
  expectedAt?: string | null;
  /** 3.12 — on no supplier order: the "Ordered" step is NOT reached. */
  notOrdered?: boolean;
  /** fn_order_line_location: the bin the part was scanned into, or its main shelf (1.1). */
  location?: string | null;
  locationKind?: 'SHELF' | 'SUPPLIER' | 'ORDER_BIN' | null;
}

/** Statuses the hand-over can take a part from: on our shelf, received, or picked into the bin. */
export const HERE = new Set(['RESERVED', 'RECEIVED', 'PICKED']);
const CLOSED = new Set([
  'DELIVERED',
  'CANCELLED',
  'CLOSED_SHORT',
  'NOT_FOUND',
  'CONVERTED',
  'RETURNED',
]);
const outstanding = (l: SheetLine) =>
  Math.max(0, l.quantity - (l.quantityShort ?? 0) - (l.quantityDelivered ?? 0));
const norm = (s: string | null | undefined) => (s ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

export interface SheetFigures {
  handedOver: number;
  ready: number;
  expected: number;
  nextExpectedAt: string | null;
  readyLines: SheetLine[];
}

export function sheetFigures(lines: SheetLine[] | undefined): SheetFigures {
  const goods = (lines ?? []).filter((l) => l.productId && l.lineType !== 'SHIPPING');
  const open = goods.filter((l) => !CLOSED.has(l.status) && outstanding(l) > 0);
  const readyLines = open.filter((l) => HERE.has(l.status));
  const waiting = open.filter((l) => !HERE.has(l.status));
  const dates = waiting
    .map((l) => l.expectedAt)
    .filter((d): d is string => !!d)
    .sort();
  return {
    handedOver: goods.reduce((n, l) => n + (l.quantityDelivered ?? 0), 0),
    ready: readyLines.reduce((n, l) => n + outstanding(l), 0),
    expected: waiting.reduce((n, l) => n + outstanding(l), 0),
    nextExpectedAt: dates[0] ?? null,
    readyLines,
  };
}

export function StatusStrip({ f }: { f: SheetFigures }) {
  const { t } = useTranslation();
  const tiles: Array<[number, string, string]> = [
    [f.handedOver, t('orders.sheet.handedOver'), f.handedOver ? 'border-green-600' : ''],
    [f.ready, t('orders.sheet.ready'), f.ready ? 'border-primary bg-primary/5' : ''],
    [
      f.expected,
      f.nextExpectedAt
        ? t('orders.sheet.expectedOn', { date: formatDate(f.nextExpectedAt) })
        : t('orders.sheet.expected'),
      f.expected ? 'border-amber-500' : '',
    ],
  ];
  return (
    <div className="grid grid-cols-3 gap-3">
      {tiles.map(([n, label, tone]) => (
        <div key={label} className={cn('rounded-lg border bg-card px-3 py-2', tone)}>
          <div className="text-2xl font-bold tabular-nums">{n}</div>
          <div className="text-sm text-muted-foreground">{label}</div>
        </div>
      ))}
    </div>
  );
}

/** The per-line journey: INTERNAL Paid → Handed over; EXTERNAL Ordered → Received → In bin → Handed over. */
export function Journey({ l }: { l: SheetLine }) {
  const { t } = useTranslation();
  if (
    !l.productId ||
    l.lineType === 'SHIPPING' ||
    ['CANCELLED', 'NOT_FOUND', 'CONVERTED', 'CLOSED_SHORT', 'RETURNED'].includes(l.status)
  )
    return null;
  // 3.12 — "Ordered" used to be reached by status alone; a line no supplier order carries is
  // not ordered, and the strip must not say it is.
  if (l.source === 'EXTERNAL' && l.notOrdered && l.status === 'AWAITING_SUPPLIER') {
    return (
      <div className="mt-1 text-[11px] font-semibold text-destructive">
        {t('orders.sheet.step.notOrdered')}
      </div>
    );
  }
  const steps =
    l.source === 'INTERNAL'
      ? [t('orders.sheet.step.ordered'), t('orders.sheet.step.handedOver')]
      : [
          t('orders.sheet.step.ordered'),
          t('orders.sheet.step.received'),
          t('orders.sheet.step.inBin'),
          t('orders.sheet.step.handedOver'),
        ];
  const reached =
    l.status === 'DELIVERED'
      ? steps.length
      : l.source === 'INTERNAL'
        ? 1
        : l.status === 'PICKED'
          ? 3
          : l.status === 'RECEIVED'
            ? 2
            : 1;
  return (
    <div className="mt-1 flex items-center gap-1 text-[11px]" aria-label={steps[reached - 1]}>
      {steps.map((s, i) => (
        <span key={s} className="flex items-center gap-1">
          {i > 0 && <span className={cn('h-0.5 w-4', i < reached ? 'bg-green-600' : 'bg-muted')} />}
          <span
            className={cn(
              i < reached ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground',
              i === reached - 1 && 'font-semibold'
            )}
          >
            {i < reached ? '✓ ' : ''}
            {s}
          </span>
        </span>
      ))}
    </div>
  );
}

/**
 * The one main action. Parts here → hand them over (with the balance a cash customer owes for
 * them); nothing here → say when the next part is expected; nothing left → say so.
 */
export function PrimaryAction({
  f,
  canHandOver,
  balance,
  onHandOver,
}: {
  f: SheetFigures;
  canHandOver: boolean;
  balance: number | null;
  onHandOver: () => void;
}) {
  const { t } = useTranslation();
  if (f.ready > 0 && canHandOver) {
    return (
      <button
        type="button"
        onClick={onHandOver}
        className="w-full rounded-lg bg-primary px-4 py-3 text-base font-semibold text-primary-foreground hover:opacity-90"
      >
        {t('orders.sheet.handOver', { count: f.ready })}
        {balance && balance > 0.005
          ? ` — ${t('orders.sheet.balance', { amount: formatEUR(balance) })}`
          : ''}
      </button>
    );
  }
  if (f.ready === 0 && f.expected > 0) {
    return (
      <button
        type="button"
        disabled
        className="w-full rounded-lg border px-4 py-3 text-base text-muted-foreground"
      >
        {f.nextExpectedAt
          ? t('orders.sheet.nothingYetOn', { date: formatDate(f.nextExpectedAt) })
          : t('orders.sheet.nothingYet')}
      </button>
    );
  }
  if (f.ready === 0 && f.expected === 0 && f.handedOver > 0) {
    return (
      <p className="rounded-lg bg-green-50 px-4 py-3 text-base text-green-800 dark:bg-green-950/30 dark:text-green-300">
        {t('orders.sheet.allHandedOver')}
      </p>
    );
  }
  return null;
}

type Step = 1 | 2 | 3 | 4;
const TENDERS = ['CASH', 'CARD', 'CHECK'] as const;

/**
 * The hand-over, in the prototype's four steps — the server applies every rule:
 *   1 Identify   pickup code / ID in the order's name / the garage's collector (E8; only on a
 *                collection — goods set aside and collected later);
 *   2 Scan       every part out of the bin (INV-41); a code that is not on this order is refused
 *                on the spot; what is not scanned stays in the bin (partial hand-over, E8);
 *   3 Balance    a cash customer pays for the SCANNED parts (Invariant #11), the figure from
 *                /payment-quote/handover; an on-account customer signs the delivery note;
 *   4 Done       delivery note, receipt, what is still to come.
 */
export function HandoverWizard({
  orderId,
  orderNumber,
  lines,
  collection,
  onClose,
  onDone,
}: {
  orderId: string;
  orderNumber: string;
  lines: SheetLine[];
  collection: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const audio = useAudioFeedback();
  const [step, setStep] = useState<Step>(collection ? 1 : 2);
  const [pickupCode, setPickupCode] = useState('');
  const [idName, setIdName] = useState('');
  const [collector, setCollector] = useState('');
  const [scans, setScans] = useState<Array<{ code: string; lineId: string }>>([]);
  const [typed, setTyped] = useState('');
  const [bad, setBad] = useState('');
  const [due, setDue] = useState<{ onAccount: boolean; dueNowTTC: number } | null>(null);
  const [tender, setTender] = useState<(typeof TENDERS)[number]>('CARD');
  const [busy, setBusy] = useState(false);
  /* The balance was collected but the hand-over was then refused (wrong code, a race): pressing
     the button again must retry the hand-over only, never take the money twice. Cleared whenever
     the due figure is asked again — that figure already nets what was paid. */
  const [collected, setCollected] = useState(false);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  const [result, setResult] = useState<{
    noteId: string | null;
    note: string | null;
    receipt: string | null;
    receiptId: string | null;
    invoiceId: string | null;
    awaiting: number;
  } | null>(null);
  const scanField = useRef<HTMLInputElement>(null);

  const ready = useMemo(
    () => lines.filter((l) => HERE.has(l.status) && outstanding(l) > 0),
    [lines]
  );
  const scannedOf = (lineId: string) => scans.filter((s) => s.lineId === lineId).length;
  const shipment = ready
    .map((l) => ({ lineId: l.id, qty: scannedOf(l.id) }))
    .filter((x) => x.qty > 0);

  useEffect(() => {
    if (step === 2) scanField.current?.focus();
  }, [step, scans.length]);

  const fail = (err: unknown) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const r = (err as any)?.response;
    setError(describeApiError(r?.data?.code, r?.data?.message, r?.status));
    audio.error();
  };

  const addScan = (raw: string) => {
    const c = raw.trim();
    setTyped('');
    if (!c) return;
    const n = norm(c);
    const line = ready.find(
      (l) =>
        [l.productEan, l.productEan2, l.productSku, l.manufacturerRef].some((k) => norm(k) === n) &&
        scannedOf(l.id) < outstanding(l)
    );
    if (!line) {
      const known = ready.some((l) =>
        [l.productEan, l.productEan2, l.productSku, l.manufacturerRef].some((k) => norm(k) === n)
      );
      setBad(
        known
          ? t('orders.sheet.wizard.allScanned', { code: c })
          : t('orders.sheet.wizard.notInOrder', { code: c, order: orderNumber })
      );
      audio.error();
      return;
    }
    setBad('');
    setScans((s) => [...s, { code: c, lineId: line.id }]);
    audio.success();
  };

  const toBalance = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await apiClient.post<{ onAccount: boolean; dueNowTTC: number }>(
        `/orders/${orderId}/payment-quote/handover`,
        { lines: shipment }
      );
      setDue(data);
      setCollected(false);
      setStep(3);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const handOver = async (collect: number) => {
    setBusy(true);
    setError(null);
    try {
      if (collect > 0.005 && !collected) {
        // Paid for exactly these parts, handed over right after: the settlement receipt of the
        // hand-over acknowledges it, so no deposit invoice (payments.ts settleOnHandover).
        await apiClient.post(
          `/orders/${orderId}/pay`,
          { payments: [{ method: tender, amount: collect }], settleOnHandover: true },
          { headers: { 'X-Command-Id': crypto.randomUUID() } }
        );
        setCollected(true);
      }
      const counts = new Map<string, number>();
      for (const s of scans) counts.set(s.code, (counts.get(s.code) ?? 0) + 1);
      const { data } = await apiClient.post<{
        deliveryNote: { id: string; deliveryNoteNumber: string } | null;
        receipt: { id: string; receiptNumber: string } | null;
        invoice: { id: string; invoiceNumber: string } | null;
        awaitingSupplier: number;
      }>(
        `/orders/${orderId}/handover`,
        {
          scans: [...counts].map(([code, qty]) => ({ code, qty })),
          identification: {
            ...(pickupCode.trim() ? { pickupCode: pickupCode.trim() } : {}),
            ...(idName.trim() ? { idCheckedName: idName.trim() } : {}),
            ...(collector.trim() ? { collectorName: collector.trim() } : {}),
          },
        },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      setResult({
        noteId: data.deliveryNote?.id ?? null,
        note: data.deliveryNote?.deliveryNoteNumber ?? null,
        receipt: data.receipt?.receiptNumber ?? null,
        receiptId: data.receipt?.id ?? null,
        invoiceId: data.invoice?.id ?? null,
        awaiting: data.awaitingSupplier,
      });
      setStep(4);
      audio.success();
    } catch (err) {
      fail(err);
      // A refused identification belongs to step 1; anything else stays where it happened.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const code = (err as any)?.response?.data?.code as string | undefined;
      if (code && /PICKUP|COLLECTOR|IDENTIFICATION/.test(code)) setStep(1);
    } finally {
      setBusy(false);
    }
  };

  const names = [
    t('orders.sheet.wizard.identify'),
    t('orders.sheet.wizard.scan'),
    t('orders.sheet.wizard.balance'),
    t('orders.sheet.wizard.done'),
  ];
  const btn = 'min-h-12 rounded-lg px-4 text-base font-medium disabled:opacity-50';

  let body: React.ReactNode = null;
  let footer: React.ReactNode = null;
  if (step === 1) {
    body = (
      <div className="space-y-3 text-base">
        <p className="text-sm text-muted-foreground">{t('orders.sheet.wizard.identifyHint')}</p>
        <label className="block">
          <span className="text-sm">{t('orders.sheet.wizard.pickupCode')}</span>
          <Input
            inputMode="numeric"
            maxLength={4}
            className="h-14 text-center text-3xl tracking-[0.4em]"
            value={pickupCode}
            onChange={(e) => setPickupCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
          />
        </label>
        <label className="block">
          <span className="text-sm">{t('orders.sheet.wizard.idChecked')}</span>
          <Input value={idName} onChange={(e) => setIdName(e.target.value)} />
        </label>
        <label className="block">
          <span className="text-sm">{t('orders.sheet.wizard.collector')}</span>
          <Input value={collector} onChange={(e) => setCollector(e.target.value)} />
        </label>
      </div>
    );
    footer = (
      <>
        <button type="button" className={cn(btn, 'border')} onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className={cn(btn, 'bg-primary text-primary-foreground')}
          disabled={!pickupCode && !idName.trim() && !collector.trim()}
          onClick={() => {
            setError(null);
            setStep(2);
          }}
        >
          {t('orders.sheet.wizard.continue')}
        </button>
      </>
    );
  } else if (step === 2) {
    body = (
      <div className="space-y-3 text-base">
        <p className="text-sm text-muted-foreground">{t('orders.sheet.wizard.scanHint')}</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            addScan(typed);
          }}
          className="flex gap-2"
        >
          <Input
            ref={scanField}
            autoComplete="off"
            className="h-12 flex-1 text-lg"
            placeholder={t('orders.sheet.wizard.scanPlaceholder')}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
          <button type="submit" className={cn(btn, 'bg-primary text-primary-foreground')}>
            {t('orders.sheet.wizard.validate')}
          </button>
        </form>
        {bad && (
          <p role="alert" className="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {bad}
          </p>
        )}
        <ul className="space-y-2">
          {ready.map((l) => {
            const n = scannedOf(l.id);
            const all = n >= outstanding(l);
            return (
              <li
                key={l.id}
                className={cn(
                  'flex items-center gap-3 rounded-lg border-2 px-3 py-2',
                  all ? 'border-green-600 bg-green-50 dark:bg-green-950/30' : ''
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{l.productName}</div>
                  <div className="text-xs text-muted-foreground">
                    {[l.productSku, l.productEan && `EAN ${l.productEan}`]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  {/* Where the part is NOW (1.1): in the customer's bin — scan it on the way
                      out; picked for a counter sale with no bin — the preparer brought it to the
                      counter (option 3); or still on its shelf — fetch it, then scan it. */}
                  <span
                    className={cn(
                      'mt-1 inline-block rounded px-1.5 py-0.5 text-xs font-medium',
                      l.locationKind === 'ORDER_BIN' || l.status === 'PICKED'
                        ? 'bg-primary/10 text-primary'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {l.locationKind === 'ORDER_BIN'
                      ? t('orders.sheet.wizard.inBin', { bin: l.location })
                      : l.status === 'PICKED'
                        ? t('orders.sheet.wizard.pickedAtCounter')
                        : l.location
                          ? t('orders.sheet.wizard.onShelf', { shelf: l.location })
                          : t('orders.sheet.wizard.onShelfUnknown')}
                  </span>
                </div>
                <span className="tabular-nums text-sm">
                  {n} / {outstanding(l)}
                </span>
                {n > 0 && (
                  <button
                    type="button"
                    className="text-sm text-primary underline"
                    onClick={() => {
                      const i = scans.map((s) => s.lineId).lastIndexOf(l.id);
                      setScans((s) => s.filter((_, k) => k !== i));
                    }}
                  >
                    {t('orders.sheet.wizard.undo')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        {shipment.length > 0 && ready.some((l) => scannedOf(l.id) < outstanding(l)) && (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {t('orders.sheet.wizard.notScannedStays')}
          </p>
        )}
      </div>
    );
    footer = (
      <>
        <button
          type="button"
          className={cn(btn, 'border')}
          onClick={() => (collection ? setStep(1) : onClose())}
        >
          {collection ? t('orders.sheet.wizard.back') : t('common.cancel')}
        </button>
        <button
          type="button"
          className={cn(btn, 'bg-primary text-primary-foreground')}
          disabled={busy || scans.length === 0}
          onClick={() => void toBalance()}
        >
          {t('orders.sheet.wizard.continueCount', {
            n: scans.length,
            of: ready.reduce((s, l) => s + outstanding(l), 0),
          })}
        </button>
      </>
    );
  } else if (step === 3 && due) {
    const collect = due.onAccount ? 0 : due.dueNowTTC;
    body = (
      <div className="space-y-3 text-base">
        {due.onAccount ? (
          <p>{t('orders.sheet.wizard.onAccount')}</p>
        ) : collect > 0.005 ? (
          <>
            <p className="text-sm text-muted-foreground">{t('orders.sheet.wizard.balanceHint')}</p>
            <div className="flex items-baseline justify-between border-t-2 border-foreground pt-2 text-xl font-bold">
              <span>{t('orders.sheet.wizard.toCollect')}</span>
              <span className="tabular-nums">{formatEUR(collect)}</span>
            </div>
            <label className="block">
              <span className="text-sm">{t('orders.detail.payment.method')}</span>
              <Select
                value={tender}
                onChange={(e) => setTender(e.target.value as (typeof TENDERS)[number])}
              >
                {TENDERS.map((m) => (
                  <option key={m} value={m}>
                    {enumLabel('paymentMethod', m)}
                  </option>
                ))}
              </Select>
            </label>
          </>
        ) : (
          <p>{t('orders.sheet.wizard.nothingToCollect')}</p>
        )}
      </div>
    );
    footer = (
      <>
        <button type="button" className={cn(btn, 'border')} onClick={() => setStep(2)}>
          {t('orders.sheet.wizard.back')}
        </button>
        <button
          type="button"
          className={cn(btn, 'bg-primary text-primary-foreground')}
          disabled={busy}
          onClick={() => void handOver(collect)}
        >
          {collect > 0.005 && !collected
            ? t('orders.sheet.wizard.collectAndHand', {
                amount: formatEUR(collect),
                method: enumLabel('paymentMethod', tender),
              })
            : t('orders.sheet.wizard.handOverNow')}
        </button>
      </>
    );
  } else if (step === 4 && result) {
    body = (
      <div className="space-y-2 text-base">
        <p className="text-lg font-semibold">{t('orders.sheet.wizard.handedOver')}</p>
        {result.note && <p>{t('orders.sheet.wizard.note', { number: result.note })}</p>}
        {result.receipt && <p>{t('orders.sheet.wizard.receipt', { number: result.receipt })}</p>}
        <p className="text-sm text-muted-foreground">
          {result.awaiting > 0
            ? t('orders.sheet.wizard.stillComing', { count: result.awaiting })
            : t('orders.sheet.wizard.nothingLeft')}
        </p>
      </div>
    );
    const openPdf = (path: string) =>
      apiClient
        .get(path, { responseType: 'blob' })
        .then((res) => {
          const url = URL.createObjectURL(res.data as Blob);
          window.open(url, '_blank', 'noopener');
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        })
        .catch(fail);
    // 2026-10-08 — one hand-over, one document, by client type: the particulier's ticket, the
    // pro comptant's invoice, the priced BL of a customer on account.
    const customerDoc = result.receiptId
      ? `/receipts/${result.receiptId}/pdf`
      : result.invoiceId
        ? `/invoices/${result.invoiceId}/order-pdf`
        : result.noteId
          ? `/delivery-notes/${result.noteId}/client-pdf`
          : null;
    footer = (
      <>
        {customerDoc && (
          <button
            type="button"
            className={cn(btn, 'bg-primary text-primary-foreground')}
            onClick={() => void openPdf(customerDoc)}
          >
            {result.receiptId
              ? t('orders.sheet.wizard.printTicket')
              : result.invoiceId
                ? t('orders.sheet.wizard.printInvoice')
                : t('orders.sheet.wizard.printPricedNote')}
          </button>
        )}
        {/* The driver's copy — the delivery note WITHOUT prices. */}
        {result.noteId && (
          <button
            type="button"
            className={cn(btn, 'border')}
            onClick={() => {
              apiClient
                .get(`/delivery-notes/${result.noteId}/pdf`, { responseType: 'blob' })
                .then((res) => {
                  const url = URL.createObjectURL(res.data as Blob);
                  window.open(url, '_blank', 'noopener');
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                })
                .catch(fail);
            }}
          >
            {t('orders.sheet.wizard.printNote')}
          </button>
        )}
        <button type="button" className={cn(btn, 'border')} onClick={onDone}>
          {t('orders.sheet.wizard.close')}
        </button>
      </>
    );
  }

  return (
    <Modal
      open
      wide
      title={t('orders.sheet.wizard.title', { number: orderNumber })}
      subtitle={
        <ol className="mt-2 flex gap-2 text-xs">
          {names.map((n, i) => (
            <li
              key={n}
              className={cn(
                'flex-1 rounded border-t-4 pt-1 text-center',
                i + 1 < step
                  ? 'border-green-600 text-green-700 dark:text-green-400'
                  : i + 1 === step
                    ? 'border-primary font-semibold'
                    : 'border-muted text-muted-foreground'
              )}
            >
              {n}
            </li>
          ))}
        </ol>
      }
      onClose={step === 4 ? onDone : onClose}
      footer={footer}
    >
      {body}
      {error && (
        <div className="mt-3 rounded border border-destructive/40 bg-destructive/10 p-3">
          <div className="text-sm font-medium text-destructive">{error.title}</div>
          <div className="mt-0.5 text-sm text-muted-foreground">{error.message}</div>
        </div>
      )}
    </Modal>
  );
}
